'use client';

import { useState, useEffect } from 'react';
import { Copy, Check, AlertCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { jsMinifyContent } from '../../data/js-minify-content';
import Link from 'next/link';

export default function JsMinifier() {
    const [input, setInput] = useState('');
    const [output, setOutput] = useState('');
    const [error, setError] = useState('');
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        const savedInput = localStorage.getItem('jsMinifierInput');
        if (savedInput) {
            setInput(savedInput);
        }
    }, []);

    const minifyJs = () => {
        try {
            if (!input.trim()) {
                setError('Please enter some JavaScript to minify');
                return;
            }

            let minified = input;

            // Remove multi-line comments
            minified = minified.replace(/\/\*[\s\S]*?\*\//g, '');

            // Remove single-line comments (safer)
            minified = minified.replace(/(^|\s)\/\/.*$/gm, '');

            // Collapse whitespace
            minified = minified.replace(/\s+/g, ' ');

            // Remove spaces around punctuation
            minified = minified.replace(/\s*([{}:;,])\s*/g, '$1');

            // Remove unnecessary semicolons
            minified = minified.replace(/;}/g, '}');

            // Trim
            minified = minified.trim();

            setOutput(minified);
            setError('');

            localStorage.setItem('jsMinifierInput', input);
            toast.success('JavaScript minified successfully!');
        } catch (err) {
            setError('Error minifying JavaScript: ' + (err as Error).message);
            setOutput('');
        }
    };

    const copyToClipboard = () => {
        if (!output) return;

        navigator.clipboard.writeText(output).then(() => {
            setCopied(true);
            toast.success('Copied to clipboard!');
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const clearAll = () => {
        setInput('');
        setOutput('');
        setError('');
        localStorage.removeItem('jsMinifierInput');
    };

    return (
        <div className="min-h-screen bg-[#F9FAFB] py-10 text-slate-900 dark:bg-slate-950 dark:text-slate-50">
            <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">

                {/* Page Header */}
                <div className="mb-8 text-center">
                    <ToolPageIntro title="JavaScript Minifier">
                Minify JavaScript code to reduce file size and improve website performance. Our free JS minifier removes whitespace, comments, and optimizes code.
            </ToolPageIntro>
                </div>

                <div className="space-y-6">

                    {/* Tool Card */}
                    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">

                        {/* Toolbar */}
                        <div className="border-b border-slate-200 bg-slate-50/80 px-4 py-3 dark:border-slate-800 dark:bg-slate-900/80">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <div className="text-left">
                                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                                        Minify your JavaScript
                                    </p>
                                    <p className="text-xs text-slate-500 dark:text-slate-400">
                                        Paste JS on the left and get compressed output on the right.
                                    </p>
                                </div>

                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={clearAll}
                                        className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                                    >
                                        Clear all
                                    </button>

                                    <button
                                        onClick={minifyJs}
                                        className="inline-flex items-center rounded-full bg-[#4F46E5] px-4 py-1.5 text-xs font-semibold text-white hover:bg-[#4338CA]"
                                    >
                                        Minify JS
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Grid */}
                        <div className="grid grid-cols-1 divide-x divide-slate-200 dark:divide-slate-800 lg:grid-cols-2">

                            {/* Input */}
                            <div className="p-4">
                                <h3 className="mb-3 text-sm font-medium text-slate-900 dark:text-slate-50">
                                    Input JavaScript
                                </h3>

                                <textarea
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    className="h-96 w-full resize-none rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-mono focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/20 dark:border-slate-700 dark:bg-slate-800"
                                    placeholder="Paste your JavaScript code here..."
                                    spellCheck={false}
                                />
                            </div>

                            {/* Output */}
                            <div className="p-4 bg-slate-50/50 dark:bg-slate-900/50">

                                <div className="mb-3 flex items-center justify-between">
                                    <h3 className="text-sm font-medium text-slate-900 dark:text-slate-50">
                                        Minified JavaScript
                                    </h3>

                                    <button
                                        onClick={copyToClipboard}
                                        disabled={!output}
                                        className="inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs"
                                    >
                                        {copied ? (
                                            <>
                                                <Check className="h-3.5 w-3.5" />
                                                Copied!
                                            </>
                                        ) : (
                                            <>
                                                <Copy className="h-3.5 w-3.5" />
                                                Copy
                                            </>
                                        )}
                                    </button>
                                </div>

                                <textarea
                                    value={output}
                                    readOnly
                                    className="h-96 w-full resize-none rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-mono dark:border-slate-700 dark:bg-slate-800"
                                    placeholder="Your minified JavaScript will appear here..."
                                />

                                {error && (
                                    <div className="mt-3 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800/30 dark:bg-red-900/20">
                                        <AlertCircle className="h-4 w-4 text-red-500" />
                                        <p className="text-sm text-red-700">{error}</p>
                                    </div>
                                )}

                            </div>
                        </div>
                    </div>

                    {/* SEO Content */}
                    <InformationalContent
                        title={jsMinifyContent.title}
                        description={jsMinifyContent.description}
                        content={jsMinifyContent.content}
                        faqs={jsMinifyContent.faqs}
                        relatedTools={jsMinifyContent.relatedTools}
                    />

                </div>
            </div>
        </div>
    );
}